import React from 'react';
import { Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  FUEL_COLORS, generateAssetColor, formatDateTime, formatPrice,
  useContainerWidth, EmptyState, CHART_MARGINS, defaultLegendProps,
  tickLabelStyle, rotatedTickLabelStyle,
} from './shared';

/**
 * Capture Price Chart
 *
 * Shows generation-weighted average price (capture price) per asset
 * vs the flat regional average. The gap reveals the "cannibalization"
 * discount for renewables or the premium earned by peaking plants.
 */
const CapturePriceChart = ({ data, assets, frequency }) => {
  const { ref, width } = useContainerWidth();

  if (!data || data.length === 0) return <EmptyState message="No capture price data available" />;

  const assetLookup = {};
  (assets || []).forEach((a, i) => { assetLookup[a.asset_id] = { ...a, idx: i }; });

  // Pivot: { bucket → { asset_id → {capture, regional} } }
  const buckets = {};
  const assetIds = new Set();
  data.forEach(({ date_time, asset_id, capture_price, regional_avg_price }) => {
    const key = String(date_time);
    if (!buckets[key]) buckets[key] = {};
    buckets[key][asset_id] = { capture_price, regional_avg_price };
    assetIds.add(asset_id);
  });

  const sortedBuckets = Object.keys(buckets).sort();
  const sortedAssets = Array.from(assetIds).sort();
  const xLabels = sortedBuckets.map((b) => formatDateTime(b, frequency));

  const series = [];

  sortedAssets.forEach((aid, idx) => {
    const info = assetLookup[aid];
    const color = info?.fuel_code ? (FUEL_COLORS[info.fuel_code] || generateAssetColor(idx)) : generateAssetColor(idx);
    series.push({
      type: 'bar',
      label: `${info?.name || aid} (Capture)`,
      data: sortedBuckets.map((b) => buckets[b][aid]?.capture_price ?? null),
      color,
    });
  });

  // Regional average line — use first asset's regional data
  const firstAsset = sortedAssets[0];
  series.push({
    type: 'line',
    label: 'Regional Avg',
    data: sortedBuckets.map((b) => buckets[b][firstAsset]?.regional_avg_price ?? null),
    color: '#ef4444',
    curve: 'monotone',
  });

  return (
    <Box ref={ref} sx={{ width: '100%', height: 420, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <BarChart
          width={width}
          height={420}
          series={series}
          xAxis={[{
            scaleType: 'band',
            data: xLabels,
            tickLabelStyle: rotatedTickLabelStyle,
            tickNumber: xLabels.length > 30 ? 30 : undefined,
          }]}
          yAxis={[{ valueFormatter: formatPrice, tickLabelStyle }]}
          margin={CHART_MARGINS}
          slotProps={{ legend: { ...defaultLegendProps } }}
        />
      )}
    </Box>
  );
};

export default CapturePriceChart;
